import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import plannerApi, { track } from "@/lib/plannerApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { fmtINR, fmtQty } from "@/lib/portalApi";
import { PlannerShell } from "@/components/planner/PlannerShell";
import { QuoteStatusPill } from "@/components/planner/QuoteStatusPill";

const MIN_ORDER = 4000;

export default function PlannerQuote() {
    const { id } = useParams();
    const loc = useLocation();
    const { client, loading } = usePortalAuth();
    const [q, setQ] = useState(null);
    const [pay, setPay] = useState("upi");
    const [busy, setBusy] = useState(false);
    const load = () => plannerApi.get(`/quotes/${id}`).then((r) => setQ(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Not found"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (client) load(); }, [client, id]);

    if (!loading && !client) return <Navigate to={`/portal/signin?next=${encodeURIComponent(loc.pathname)}`} replace/>;
    if (!q) return <PlannerShell><div className="p-16 text-center"><Loader2 className="inline animate-spin text-slate-400"/></div></PlannerShell>;

    const act = async (path, body) => {
        setBusy(true);
        try {
            await plannerApi.post(`/quotes/${id}/${path}`, body);
            if (path === "accept") { track("quote_approved", q.planner_type); toast.success("Order placed — we'll schedule your delivery"); }
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
        finally { setBusy(false); }
    };
    const quote = q.quote;
    const byLine = Object.fromEntries((quote?.lines || []).map((l) => [l.line_id, l]));
    const shown = q.status !== "requested" && quote;
    const belowMin = shown && quote.total < MIN_ORDER;

    return (
        <PlannerShell>
            <div data-testid="planner-quote-page">
                <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-3xl font-black text-slate-900" style={{ fontFamily: "'Chivo',sans-serif" }}>Quotation {q.number}</h1>
                    <QuoteStatusPill status={q.status}/>
                </div>
                <p className="text-sm text-slate-500 mt-1">From list “{q.list_name}” · requested {q.submitted_at.slice(0, 10)}</p>
                {q.status === "requested" && (
                    <div className="mt-5 rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900" data-testid="planner-quote-waiting">
                        Our team is checking every item, brand and pack size. You'll see the full quotation here once it's ready.
                        {q.first_order_eligible && " As a first-time customer you may be eligible for ₹500 off."}
                    </div>
                )}
                {q.status === "accepted" && (
                    <div className="mt-5 rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900 flex items-center gap-2" data-testid="planner-quote-accepted">
                        <CheckCircle2 size={16}/> Accepted — your order is with our team. Track it under <Link to="/portal/orders" className="font-bold underline">My Orders</Link>.
                    </div>
                )}
                <div className="mt-5 rounded-2xl bg-white border border-slate-200 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="planner-quote-lines">
                        <thead className="text-[10px] uppercase tracking-widest text-slate-500 text-left">
                            <tr><th className="p-3">Item</th><th className="p-3 text-right">Qty</th>
                                {shown && <><th className="p-3 text-right">Rate</th><th className="p-3 text-right">Amount</th></>}</tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {q.snapshot.lines.map((l, i) => {
                                const ql = byLine[l.line_id];
                                const na = shown && (!ql || !ql.available);
                                return (
                                    <tr key={l.line_id} className={na ? "text-slate-400" : ""} data-testid={`planner-quote-line-${i}`}>
                                        <td className="p-3"><div className={`font-semibold ${na ? "line-through" : ""}`}>{l.name}</div>
                                            <div className="text-[11px] text-slate-500">{[l.brand_preference, l.pack_size && `${l.pack_size} ${l.uom}`, l.department].filter(Boolean).join(" · ")}
                                                {na && " · Not available"}{ql?.note && ` · ${ql.note}`}</div></td>
                                        <td className="p-3 text-right font-mono">{fmtQty(l.qty)}</td>
                                        {shown && <><td className="p-3 text-right font-mono">{na ? "—" : fmtINR(ql.rate)}</td>
                                            <td className="p-3 text-right font-mono">{na ? "—" : fmtINR(ql.amount)}</td></>}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {shown && (
                    <div className="mt-4 rounded-2xl bg-white border border-slate-200 p-5 max-w-md ml-auto text-sm space-y-1.5" data-testid="planner-quote-totals">
                        <Row k="Subtotal" v={quote.subtotal}/><Row k="GST" v={quote.tax}/>
                        {quote.discount > 0 && <Row k="Discount" v={quote.discount} neg cls="text-emerald-700"/>}
                        <Row k="Total" v={quote.total} cls="text-lg font-black" tid="planner-quote-total"/>
                        <div className="text-[11px] text-slate-500">Valid until {quote.valid_until}{quote.notes && ` · ${quote.notes}`}</div>
                    </div>
                )}
                {q.status === "quoted" && (
                    <div className="mt-5 max-w-md ml-auto">
                        {belowMin ? (
                            <p className="text-sm text-red-700" data-testid="planner-quote-below-min">Minimum confirmed order is ₹4,000. Add more items to your list and request a new quotation.</p>
                        ) : (
                            <div className="flex gap-2 mb-3" data-testid="planner-quote-payment">
                                {[["upi", "UPI"], ["cod", "Cash on delivery"]].map(([k, l]) => (
                                    <button key={k} onClick={() => setPay(k)} data-testid={`planner-pay-${k}`}
                                            className={`flex-1 py-2 rounded-xl border-2 text-sm font-bold ${pay === k ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-600"}`}>{l}</button>
                                ))}
                            </div>
                        )}
                        <div className="flex gap-2">
                            <button disabled={busy} onClick={() => act("decline", {})} className="flex-1 py-3 rounded-xl border border-slate-300 font-bold text-slate-700" data-testid="planner-quote-decline">Decline</button>
                            <button disabled={busy || belowMin} onClick={() => act("accept", { payment_method: pay })} data-testid="planner-quote-accept"
                                    className="flex-[2] py-3 rounded-xl bg-emerald-600 text-white font-black disabled:opacity-40 inline-flex items-center justify-center gap-2">
                                {busy && <Loader2 size={14} className="animate-spin"/>} Accept &amp; place order
                            </button>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-2">Planned delivery. Nothing is charged until delivery.</p>
                    </div>
                )}
            </div>
        </PlannerShell>
    );
}

const Row = ({ k, v, cls = "", tid, neg = false }) => (
    <div className={`flex justify-between ${cls}`}><span>{k}</span><span className="font-mono" data-testid={tid}>{neg ? "− " : ""}{fmtINR(v)}</span></div>
);
